import React, { useEffect, useState, useRef, useCallback } from 'react';
import { TableRowsSkeleton } from '../../../components/skeleton/skeleton';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import dashboardApi from '../../../services/dashboardApi';
import orderApi from '../../../services/orderApi';
import { setStartAndEndDate } from '../../../utils/setStartAndEndDate';
import {
  PageHeader,
  StatCard,
  TWButton,
  TWSelect,
  TWModal,
  SegmentedToggle,
} from '../../../components/ui';
import { formatCurrency } from '../../../utils/baseUtils';

const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');
dayjs.Ls.en.weekStart = 1;
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const TAB_OPTIONS = [
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
  { value: 'Week', label: 'Week' },
  { value: 'Last Week', label: 'Last Week' },
];

const Reports2 = () => {
  const navigate = useNavigate();
  const cancelButtonRef = useRef(null);

  const [activeTab, setActiveTab] = useState('Today');
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState({
    reportsLoading: true,
    exportTaxReportLoading: false,
  });
  const [exportForm, setExportForm] = useState({ year: dayjs().year() });
  const [filter, setFilter] = useState({
    startdate: setStartAndEndDate(dayjs().startOf('day'), 'startdate', '0'),
    enddate: setStartAndEndDate(dayjs(), 'enddate', '0'),
  });
  const [dialogs, setDialogs] = useState({ exportTaxReport: false });

  const getReports = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, reportsLoading: true }));
      const res = await dashboardApi.getReports(filter);
      setReports(res.data || {});
    } catch (error) {
      setReports({});
    } finally {
      setLoading((prev) => ({ ...prev, reportsLoading: false }));
    }
  }, [filter]);

  const selectDateFromTabs = (selected) => {
    if (!selected) return;
    let enddate = dayjs();
    let startdate;
    switch (selected) {
      case 'Today':
        startdate = enddate.startOf('day');
        break;
      case 'Yesterday':
        startdate = enddate.subtract(1, 'day').startOf('day');
        enddate = enddate.subtract(1, 'day').endOf('day');
        break;
      case 'Week':
        startdate = enddate.startOf('week');
        break;
      case 'Last Week':
        startdate = enddate.subtract(1, 'week').startOf('week');
        enddate = enddate.subtract(1, 'week').endOf('week');
        break;
      default:
        return;
    }
    setActiveTab(selected);
    setFilter((prev) => ({
      ...prev,
      startdate: setStartAndEndDate(startdate, 'startdate', '0'),
      enddate: setStartAndEndDate(enddate, 'enddate', '0'),
    }));
  };

  const onCloseExportTaxReport = () => {
    setDialogs((prev) => ({ ...prev, exportTaxReport: false }));
    setExportForm({ year: dayjs().year() });
  };

  const onSubmitExportTaxReport = async () => {
    setLoading((prev) => ({ ...prev, exportTaxReportLoading: true }));
    try {
      const csvLink = await orderApi.getExportTaxReport({
        year: exportForm.year,
      });
      window.open(csvLink.data, '_blank');
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, exportTaxReportLoading: false }));
    }
  };

  useEffect(() => {
    getReports();
  }, [activeTab, getReports]);

  const headerActions = (
    <>
      <TWButton
        icon={PlayCircleIcon}
        label="Live Time Report"
        onClick={() => navigate('/dashboard/reports/live-time')}
      />
      <TWButton
        icon={ArrowDownTrayIcon}
        label="Export Tax Report"
        onClick={() =>
          setDialogs((prev) => ({ ...prev, exportTaxReport: true }))
        }
      />
      <TWButton
        icon={DocumentChartBarIcon}
        label="Company Sales"
        onClick={() => navigate('/dashboard/reports/company-sales')}
      />
    </>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-white dark:bg-slate-800">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Reports"
          description="Track your sales metrics and optimize your business performance."
          actions={headerActions}
        />

        {loading.reportsLoading ? (
          <TableRowsSkeleton count={8} />
        ) : (
          <div className="space-y-6">
            <SegmentedToggle
              ariaLabel="Date range"
              value={activeTab}
              onChange={selectDateFromTabs}
              options={TAB_OPTIONS}
            />

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                icon={ChartBarIcon}
                tone="blue"
                label="Gross Sales"
                value={formatCurrency(reports.ordertotal)}
              />
              <StatCard
                icon={DocumentChartBarIcon}
                tone="violet"
                label="Orders"
                value={Number(reports.ordercount || 0).toLocaleString('en-US')}
              />
              <StatCard
                icon={ArrowDownTrayIcon}
                tone="emerald"
                label="Payouts"
                value={formatCurrency(reports.payout)}
              />
              <StatCard
                icon={ChartBarIcon}
                tone="amber"
                label="Profit"
                value={formatCurrency(reports.profit)}
              />
            </div>
          </div>
        )}
      </div>

      <TWModal
        open={dialogs.exportTaxReport}
        onClose={onCloseExportTaxReport}
        title="Export Tax Report"
        description="Export tax report for your orders"
        size="xl"
        initialFocus={cancelButtonRef}
        showCloseButton
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <TWButton
              ref={cancelButtonRef}
              label="Cancel"
              disabled={loading.exportTaxReportLoading}
              onClick={onCloseExportTaxReport}
            />
            <TWButton
              variant="primary"
              icon={ArrowDownTrayIcon}
              label="Download Export Tax Report"
              loading={loading.exportTaxReportLoading}
              onClick={onSubmitExportTaxReport}
            />
          </div>
        }
      >
        <TWSelect
          label="Year"
          placeholder="Select a year"
          defaultValue={exportForm.year}
          onChange={(val) => setExportForm({ ...exportForm, year: val })}
          options={Array.from({ length: 10 }, (_, i) => ({
            label: `${dayjs().subtract(i, 'year').year()}`,
            value: dayjs().subtract(i, 'year').year(),
          }))}
        />
      </TWModal>
    </div>
  );
};

export default Reports2;
